import ClaimLoader from './Components/ClaimLoader'
import { faPlus, faMagnifyingGlass, faWrench, faPencil, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getSaiTerminology } from '../../../config/terminology'
import { Tooltip } from 'react-tooltip'

import useAxiosPrivateKIS from '../../../hooks/useAxiosPrivateKIS'
import useAuth from '../../../hooks/useAuth'

import moment from 'moment'

import Slideout from '../../../components/SlideOut'
import ViewClaim from './Components/ViewClaim'

const Claims = () => {
  const navigate = useNavigate()
  const { createInfo } = useAuth()
  const [policyData, setPolicyData] = useState(null)
  const [department, setDepartment] = useState(null)
  const terms = getSaiTerminology(policyData)
  const axios = useAxiosPrivateKIS()
  const [activeTab, setActiveTab] = useState('current')
  const [claims, setClaims] = useState([])
  const [claim, setClaim] = useState([])
  const [slideoutOpen, setSlideoutOpen] = useState(false)
  const [viewOpenClaims, setViewOpenClaims] = useState(true)
  const [showOld, setShowOld] = useState(false)
  const [canClaim, setCanClaim] = useState(false)
  const [selectedPreviousPolicy, setSelectedPreviousPolicy] = useState(null)
  const [previousClaims, setPreviousClaims] = useState([])
  const [daysLeftToClaim, setDaysLeftToClaim] = useState(null)
  const [policyStatus, setPolicyStatus] = useState('')
  const [canClaimPrevious, setCanClaimPrevious] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchParams] = useSearchParams()
  const actionClaimId = searchParams.get('actionClaimId')
  const [isLoading, setIsLoading] = useState(true)

  const openClaim = (claim) => {
    setClaim(claim)
    setSlideoutOpen(true)
  }

  const openEdit = (claim) => {
    navigate(`/sai/claims/${claim.claim_id}/edit`, { state: { claim } })
  }

  const openCreate = () => {
    navigate('/sai/claims/new')
  }

  const closeSlideout = () => {
    setSlideoutOpen(false)
  }

  useEffect(() => {
    if (actionClaimId && claims?.length > 0) {
      const foundClaim = claims?.find(claim => claim?.id === Number(actionClaimId))
      if (foundClaim) {
        setClaim(foundClaim)
        setSlideoutOpen(true)
      }
    }
  }, [actionClaimId, claims])

  const slideoutTitle = `View Claim ${claim?.claim_reference || ''}`

  const renderSlideoutContent = () => {
    return (
      <ViewClaim
        claim={claim}
        policy={policyData}
      />
    )
  }

  const statusColors = {
    3: "bg-red-100 text-red-700",
    4: "bg-red-100 text-red-700",
    8: "bg-red-100 text-red-700",
    9: "bg-red-100 text-red-700",
    11: "bg-red-100 text-red-700",
    1: "bg-orange-100 text-orange-700",
    2: "bg-orange-100 text-orange-700",
    18: "bg-orange-100 text-orange-700",
    19: "bg-orange-100 text-orange-700",
    23: "bg-orange-100 text-orange-700",
    5: "bg-blue-100 text-blue-700",
    6: "bg-blue-100 text-blue-700",
    14: "bg-blue-100 text-blue-700",
    15: "bg-blue-100 text-blue-700",
    16: "bg-blue-100 text-blue-700",
    21: "bg-blue-100 text-blue-700",
    7: "bg-green-100 text-green-700",
    20: "bg-green-100 text-green-700",
    25: "bg-green-100 text-green-700",
    26: "bg-green-100 text-green-700",
    10: "bg-purple-100 text-purple-700",
    12: "bg-purple-100 text-purple-700",
    13: "bg-purple-100 text-purple-700",
    17: "bg-purple-100 text-purple-700",
    22: "bg-purple-100 text-purple-700",
    24: "bg-purple-100 text-purple-700",
  };

  const getStatusTag = (statusId, statusText) => {
    const colorClass = statusColors[statusId] || "bg-gray-100 text-gray-700";
    return (
      <span
        className={`text-[10px] font-semibold px-2 py-1 rounded capitalize ${colorClass}`}
      >
        {statusText}
      </span>
    );
  };

  const fetchClaims = async () => {
    try {
      const claimsRes = await axios.get(`/api/v1/staff-hub/claims/${policyData.policy_id}/list/${viewOpenClaims}?api_token=${process.env.REACT_APP_KIS_TOKEN}&q=&l=10&o=0`);

      setClaims(claimsRes.data || [])
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setClaims([]);
    }
  }

  const fetchPreviousClaims = async () => {
    try {
      const res = await axios.get(`/api/v1/staff-hub/claims/${policyData.policy_id}/old/${viewOpenClaims}?api_token=${process.env.REACT_APP_KIS_TOKEN}&q=&l=10&o=0`);

      setPreviousClaims(res.data || [])
    } catch (error) {
      console.error('Error fetching previous claims:', error)
      setPreviousClaims([])
    }
  }

  const canStillClaim = async (selectedPolicyId) => {
    try {
      const response = await axios.get(`/api/v1/staff-hub/claims/${selectedPolicyId}/can-still-claim?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      const data = response.data

      setShowOld(data.show_previous_claims)
      setCanClaim(data.can_claim)
      setDaysLeftToClaim(data.days_left_to_claim)
      setCanClaimPrevious(data.can_claim_previous)
      if (data.previous_policy_id !== null) {
        setSelectedPreviousPolicy(data.previous_policy_id)
      }
    } catch (error) {
      console.error('Could not get old claims list', error)
    }
  }

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    setPolicyData(storedPolicy)
    setDepartment(storedPolicy.department)
  }, [])

  useEffect(() => {
    if (!policyData) return

    setPolicyStatus(
      `Add ${terms.claim.singularTitle}`
    )
  }, [policyData, terms.claim.singularTitle])

  useEffect(() => {
    if (!policyData?.policy_id) return;
    let active = true;
    setIsLoading(true);
    const run = async () => {
      try {
        await Promise.allSettled([
          activeTab === 'previous' ? fetchPreviousClaims() : fetchClaims(),
          canStillClaim(policyData.policy_id),
        ]);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [activeTab, policyData?.policy_id, viewOpenClaims])

  const handleTabChange = (tab) => {
    setIsLoading(true)
    setActiveTab(tab)

    const requestType = terms.claim.singularTitle

    if (tab === 'current') {
      setViewOpenClaims(true)
      setPolicyStatus(`Add ${requestType}`)
      localStorage.setItem('policy_id', policyData?.policy_id || '')
    }

    if (tab === 'previous') {
      setViewOpenClaims(true)
      setPolicyStatus(`Add ${requestType} To Previous ${terms.policy.singularTitle}`)
      localStorage.setItem('policy_id', selectedPreviousPolicy || '')
    }
  }

  const filteredClaims = (activeTab === 'current' ? claims : previousClaims).filter(claim => {
    const query = searchQuery.toLowerCase()
    if (isLoading) {
    return <ClaimLoader />;
  }

  return (
      claim.claim_reference?.toLowerCase().includes(query) ||
      claim.staff_name?.toLowerCase().includes(query)
    )
  })

  return (
    <div className="w-full">
      <div>
        <main className="w-full space-y-4">
          <div className="p-6 w-full">
            <div className="flex mb-4">
              {canClaim === true && (
                <>
                  <button
                    onClick={() => handleTabChange('current')}
                    className={`px-4 py-2 font-semibold ${activeTab === 'current' ? 'border-b-4 border-sas text-sas' : 'text-gray-500'}`}
                  >
                    Current {terms.policy.singularTitle}
                  </button>

                  {showOld && (
                    <button
                      onClick={() => handleTabChange('previous')}
                      className={`px-4 py-2 font-semibold ${activeTab === 'previous' ? 'border-b-4 border-sas text-sas' : 'text-gray-500'}`}
                    >
                      Previous {terms.policy.singularTitle}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div className="flex items-center space-x-3">
                <div
                  onClick={() => { setIsLoading(true); setViewOpenClaims(prev => !prev); }}
                  className={`relative inline-block w-12 h-6 transition duration-300 ease-in-out`}
                >
                  <div
                    className={`absolute inset-0 rounded-full transition ${viewOpenClaims ? 'bg-sas' : 'bg-gray-300'
                      }`}
                  ></div>
                  <span
                    className={`absolute left-0 top-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-300 ${viewOpenClaims ? 'translate-x-6' : 'translate-x-0'
                      }`}
                  ></span>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Viewing your {viewOpenClaims ? 'open' : 'closed'} {terms.claim.pluralTitle}
                </span>
              </div>
              {((activeTab === 'current' && canClaim) || (activeTab === 'previous' && canClaimPrevious && daysLeftToClaim > 0)) && (
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden"
                >
                  <span className="px-4 py-2 text-sm font-medium">{policyStatus}</span>
                  <span className="bg-sas text-white px-3 py-2">
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                </button>
              )}
            </div>

            <div className="mb-4 relative w-full sm:w-72">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Enter ${terms.claim.singularLower} reference or staff name`}
                className="border border-gray-300 text-xs rounded-md pl-10 pr-3 py-1 w-full focus:outline-none focus:ring-1 focus:ring-sas focus:border-sas"
              />
            </div>
            <div className="overflow-x-auto rounded-lg">
              <table className="min-w-full text-[10px] text-left text-gray-800 overflow-hidden">
                <thead>
                  <tr className="bg-sas text-white text-xs text-start">
                    <th className="p-2 font-semibold">Reference</th>
                    <th className="p-2 font-semibold">Name</th>
                    <th className="p-2 font-semibold">Condition</th>
                    <th className="p-2 font-semibold">First Day of Absence</th>
                    <th className="p-2 font-semibold">Last Day of Absence</th>
                    <th className="p-2 font-semibold">Next Payment</th>
                    <th className="p-2 font-semibold">Status</th>
                    <th className="p-2 font-semibold">Logged</th>
                    {viewOpenClaims && ( <th className="p-2 font-semibold">Action</th> )}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr className="border-b">
                      <td colSpan="9" className="text-center py-6 text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Loading {department === 'Mutual' || department === 'GBS' ? 'Payment Requests' : 'Claims'}...
                      </td>
                    </tr>
                  ) : filteredClaims.length > 0 ? (
                    filteredClaims.map((claim, idx) => (
                      <tr key={idx} className="border-b text-center">
                        <td className="py-2">{claim.claim_reference}</td>
                        <td className="py-2">{claim.staff_name}</td>
                        <td className="py-2 whitespace-normal break-words">{claim.condition}</td>
                        <td className="py-2">{moment(claim.start_date).format('DD/MM/YYYY')}</td>
                        <td className="py-2">  {claim.end_date ? moment(claim.end_date).format('DD/MM/YYYY') : ''}</td>
                        <td className="py-2">{claim.next}</td>
                        <td className="py-2">
                          <span
                            data-tooltip-id={`status-tooltip-${claim.claim_id}`}
                            data-tooltip-content={claim.claim_status_text}
                            className="cursor-help"
                          >
                            {getStatusTag(claim.claim_status_id, claim.status?.text || "")}
                          </span>
                          <Tooltip id={`status-tooltip-${claim.claim_id}`} place="top" />
                        </td>
                        <td className="py-2">{moment(claim.created_at).format('DD/MM/YYYY')}</td>
                        {viewOpenClaims && (
                          <td className="py-2 space-x-1">
                            <>
                              {department === "Mutual" || department === "GBS" ? (
                                claim.claim_status_id < 3 || claim.claim_status_id === 12 ? (
                                  <button
                                    type="button"
                                    onClick={() => openEdit(claim)}
                                    className="text-sas hover:text-sas-dark font-medium hover:underline"
                                  >
                                    <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openClaim(claim)}
                                    className="text-sas hover:text-sas-dark font-medium hover:underline"
                                  >
                                    <FontAwesomeIcon icon={faWrench} className="h-3 w-3" />
                                  </button>
                                )
                              ) : claim.claim_status_id < 6 || claim.claim_status_id === 12 ? (
                                <button
                                  type="button"
                                  onClick={() => openEdit(claim)}
                                  className="text-sas hover:text-sas-dark font-medium hover:underline"
                                >
                                  <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openClaim(claim)}
                                  className="text-sas hover:text-sas-dark font-medium hover:underline"
                                >
                                  <FontAwesomeIcon icon={faWrench} className="h-3 w-3" />
                                </button>
                              )}
                            </>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b">
                      <td colSpan="9" className="text-center text-gray-500 py-4">
                        {department === "Mutual" || department === "GBS"
                          ? "No Payment Request Found"
                          : "No Claims Found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Slideout
            open={slideoutOpen}
            setOpen={closeSlideout}
            title={slideoutTitle}
            width="lg"
          >
            {renderSlideoutContent()}
          </Slideout>
        </main>
      </div>
    </div>
  )
}

export default Claims
